import {
  ChildEvents,
  ChildMachineSchema,
  EventKind,
  FlowObjects,
  IndexTypes,
  Kind,
} from "./../features/fsm/types";
import FlowObject, {
  actionFromJSON,
  parseFrame,
  MixedMediaObjectType,
  FrameVal,
} from "./FlowObject";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";
import Color from "./Color";
import ImageAsset, { AssetURL, PdfAsset } from "./Assets";
import { CapitalBool } from "./Flow";
import GalleryAsset from "./GalleryAsset";
import Action from "./Action";
import { UploadResponse } from "./Screens";
import { IndexName } from "../features/fsm/types";
import { Interpreter } from "xstate";
import * as Sentry from "@sentry/react"

export default class VideoFlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;
  autoPlay: boolean;
  isyoutube: CapitalBool;
  cornerRadius: number;

  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    autoPlay: boolean,
    isyoutube: CapitalBool,
    cornerRadius: number,
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = false;
    this.autoPlay = autoPlay;
    this.isyoutube = isyoutube;
    this.cornerRadius = cornerRadius;
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  static fromJSON(json: any): VideoFlowObject {
    let flowObj = Object.create(VideoFlowObject.prototype);
    return Object.assign(flowObj, json, {
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
      originalImageAsset: ImageAsset.fromJSON(json.originalImageAsset),
      croppedImageAsset: ImageAsset.fromJSON(json.croppedImageAsset),
      retinaImageAsset: ImageAsset.fromJSON(json.retinaImageAsset),
      videoAsset: ImageAsset.fromJSON(json.videoAsset),
      autoPlay: json.autoPlay,
      isyoutube: json.isyoutube,
      __forceUseImage: true,
      borderColor: Color.fromJSON(json.borderColor),
      cornerRadius: json.cornerRadius || 0,
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? VideoFlowObject.fromJSON(value) : value;
  }

  static default(src: VideoInitializer): VideoFlowObject {
    let nameSplit = src.name.split(".");
    let imgName = nameSplit.slice(0, nameSplit.length - 1).join(".") + ".jpeg";
    return new VideoFlowObject(
      guidGenerator(),
      Color.iosToCssColor("00000"),
      0,
      null,
      getCurrentDate(),
      {
        width: src.width || 200.0,
        height: src.height || 200.0,
        x: 200.0,
        y: 200.0,
      },
      ImageAsset.default(src.vidSrc, false, src.name),
      1,
      null,
      null,
      "video",
      null,
      ImageAsset.default(src.vidSrc, false, imgName),
      0,
      {
        height: 0,
        width: 0,
      },
      null,
      null,
      getCurrentDate(),
      false,
      false,
      src.isYoutube,
      0,
    );
  }

  toggleAutoplay(): VideoFlowObject {
    const oldNum = this.autoPlay ? 1 : 0;
    const newNum = !this.autoPlay ? 1 : 0;
    this.autoPlay = !this.autoPlay;
    if (
      this.isyoutube === "YES" &&
      this.videoAsset!.externalURL.__tag === "string"
    ) {
      this.videoAsset!.externalURL.value.replace(
        `autoplay=${oldNum}`,
        `autoplay=${newNum}`
      );
    }
    return this;
  }

  /**
   * Updates object to use CDN image instead of local
   *
   * Needs `indexMap` since backend provides no context to where responses should go.
   *
   * @param response [UploadResponse] a single element array with an `ssl_address` and `treemoContentID`
   * @param indexMap hashmap defining which index in upload response maps to fields on this object
   */
  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    let index = indexMap.get(IndexTypes.videoAsset);
    Sentry.addBreadcrumb({
      category: 'debug',
      message: 'media upload response',
      data: { response, indexMap },
      level: Sentry.Severity.Info,
      type: 'code',
    });
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.videoAsset!.externalURL = { __tag: "string", value: ssl_address };
      this.videoAsset!.treemoContentID = treemoContentID;
      this.videoAsset!.updated = getCurrentDate();
    }
    index = indexMap.get(IndexTypes.originalImageAsset);
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.originalImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.originalImageAsset!.treemoContentID = treemoContentID;
      this.originalImageAsset!.updated = getCurrentDate();
    }
    index = indexMap.get(IndexTypes.croppedImageAsset);
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.croppedImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.croppedImageAsset!.treemoContentID = treemoContentID;
      this.croppedImageAsset!.updated = getCurrentDate();
    }
  }
}

type VideoInitializer = {
  vidSrc: AssetURL;
  name: string;
  width?: number;
  height?: number;
  isYoutube: CapitalBool;
};
